import React, { useEffect, useState, useMemo, useId } from 'react';
import Api from '@plone/volto/helpers/Api/Api';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import { withRouter, RouteComponentProps } from 'react-router';
import { compose } from 'redux';
import { ContextNavigationLink } from './ContextNavigationLink';
import { withContextNavigation } from './withContextNavigation';
import {
  ArrowLeftIcon,
  CaretUpIcon,
  CaretDownIcon,
} from '@phosphor-icons/react';
import { MPFLink } from 'volto-ministerio-publico-federal/components/UI';
import { MenuFilter, highlightQuery, matchesQuery } from './MenuFilter';
import { NO_INCLUDE_CONTEXT_NAVIGATION } from 'volto-ministerio-publico-federal/constants';

// Tipagem para cada item do menu
interface NavigationItem {
  '@id'?: string;
  title: string;
  url?: string;
  href?: string;
  type?: string;
  items?: NavigationItem[];
  is_current?: boolean;
  is_in_path?: boolean;
  // Fora do caminho os filhos não vêm; este flag diz se há o que buscar.
  has_children?: boolean;
  description?: string;
  hidden?: boolean;
}

// Tipagem do objeto navigation
interface Navigation {
  items?: NavigationItem[];
  has_custom_name?: boolean;
  // Raiz da seção em sanfona; submenu (ou raiz com filtro) em lista plana.
  mode?: 'accordion' | 'flat';
  filter?: { label: string; placeholder: string };
  title?:
    | {
        title: string;
        relative_url?: string;
        // O título é o link para a página da raiz; a default page dela não
        // entra no menu, então o destaque de "página atual" fica aqui.
        is_current?: boolean;
        // Num submenu, o pai da raiz (um nível acima): o caminho de volta.
        parent?: { title: string; relative_url: string };
      }
    | string;
}

interface ContextNavigationProps extends RouteComponentProps {
  navigation?: Navigation;
  showSearchInput?: boolean;
}

const filterOutMediaItems = (items: NavigationItem[]): NavigationItem[] => {
  // Primeiro promove filhos de itens hidden (default pages) e depois filtra
  const promoted = items.flatMap((item) => {
    const type = item.type?.toLowerCase();
    const isExcluded = NO_INCLUDE_CONTEXT_NAVIGATION.includes(type || '');

    // Se item está oculto (default page), promove seus filhos
    if (item.hidden) {
      return item.items ? filterOutMediaItems(item.items) : [];
    }

    // Se tipo está excluído, remove item e seus filhos
    if (isExcluded) {
      return [];
    }

    // Caso contrário, mantém item e filtra recursivamente seus filhos
    return [
      {
        ...item,
        items: item.items ? filterOutMediaItems(item.items) : item.items,
      },
    ];
  });

  return promoted;
};

// Filhos que ficaram da página anterior: o destaque dela não vale mais.
const withoutCurrent = (items: NavigationItem[]): NavigationItem[] =>
  items.map((item) => ({
    ...item,
    is_current: false,
    is_in_path: false,
    items: item.items && withoutCurrent(item.items),
  }));

const hasChildren = (node: NavigationItem) =>
  !!(node.items?.length || node.has_children);

type ChildrenStatus = 'idle' | 'loading' | 'error';

// Filhos já buscados, por caminho: trocar de página remonta o menu e não deve
// buscar de novo. Só no navegador; no servidor o módulo é compartilhado entre
// visitantes com permissões diferentes.
const CHILDREN_CACHE_MS = 5 * 60 * 1000;
const childrenCache = new Map<
  string,
  { at: number; items: NavigationItem[] }
>();

export const clearChildrenCache = () => childrenCache.clear();

const getCachedChildren = (path: string) => {
  const hit = childrenCache.get(path);
  return hit && Date.now() - hit.at < CHILDREN_CACHE_MS ? hit.items : null;
};

const setCachedChildren = (path: string, items: NavigationItem[]) => {
  if (typeof window !== 'undefined') {
    childrenCache.set(path, { at: Date.now(), items });
  }
};

/**
 * Um item aberto por nível (sanfona). A cada página nova a lista volta ao
 * caminho da página ativa: o que o visitante tinha aberto fora dele fecha.
 */
const useOpenItem = (items: NavigationItem[]) => {
  const activeId =
    items.find(
      (item) => (item.is_in_path || item.is_current) && hasChildren(item),
    )?.['@id'] ?? null;
  const [openId, setOpenId] = useState<string | null>(activeId);
  // `items` muda de identidade quando chega o menu de outra página.
  useEffect(() => {
    setOpenId(activeId);
  }, [activeId, items]);
  const toggle = (id: string) =>
    setOpenId((current) => (current === id ? null : id));
  return [openId, toggle] as const;
};

interface DropdownItemProps {
  node: NavigationItem;
  isOpen: boolean;
  onToggle: () => void;
}

/**
 * Sanfona do primeiro nível. Os filhos são links: quem tem filhos ganha "›"
 * e leva ao submenu dele (a página do item, com o menu começando nela).
 */
const DropdownItem: React.FC<DropdownItemProps> = ({
  node,
  isOpen,
  onToggle,
}) => {
  const path = flattenToAppURL(node['@id'] || '');
  const [children, setChildren] = useState<NavigationItem[] | null>(
    node.items?.length ? node.items : getCachedChildren(path),
  );
  const [status, setStatus] = useState<ChildrenStatus>('idle');
  const listId = useId();

  // O menu continua montado entre páginas: os filhos que vêm com a página
  // nova substituem os anteriores.
  useEffect(() => {
    if (node.items?.length) {
      setChildren(node.items);
    } else {
      setChildren((current) => current && withoutCurrent(current));
    }
  }, [node.items]);

  // Fora do caminho os filhos só são buscados na primeira vez que o item abre.
  const loadChildren = () => {
    setStatus('loading');
    new Api()
      .get(`${path}/@contextnavigation-children`)
      .then((res: { items?: NavigationItem[] }) => {
        const items = filterOutMediaItems(res.items || []);
        setCachedChildren(path, items);
        setChildren(items);
        setStatus('idle');
      })
      .catch(() => setStatus('error'));
  };

  const toggle = () => {
    if (!isOpen && children === null && status !== 'loading') {
      loadChildren();
    }
    onToggle();
  };

  // O título navega para a pasta; só o botão da setinha abre/fecha a lista.
  const summaryClassName = `mpf-side-menu__dropdown__summary${
    node.is_current ? ' mpf-side-menu__dropdown__summary--active' : ''
  }`;

  return (
    <li className="mpf-side-menu__item mpf-side-menu__item--has-dropdown">
      <div className="mpf-side-menu__dropdown">
        <div className={summaryClassName}>
          <ContextNavigationLink props={node} folder />
          <button
            type="button"
            className="mpf-side-menu__dropdown__summary-icon"
            aria-expanded={isOpen}
            aria-controls={isOpen ? listId : undefined}
            aria-label={`${isOpen ? 'Recolher' : 'Expandir'} ${node.title}`}
            onClick={toggle}
          >
            {isOpen ? (
              <CaretUpIcon className="mpf-side-menu__dropdown__summary-ph" />
            ) : (
              <CaretDownIcon className="mpf-side-menu__dropdown__summary-ph" />
            )}
          </button>
        </div>
        {/* Só o ramo aberto vai para o DOM. */}
        {isOpen && (
          <ul
            id={listId}
            className="mpf-side-menu__dropdown__list"
            aria-busy={status === 'loading'}
          >
            {status === 'loading' && (
              <li
                className="mpf-side-menu__dropdown__item mpf-side-menu__dropdown__status"
                role="status"
              >
                Carregando…
              </li>
            )}
            {status === 'error' && (
              <li className="mpf-side-menu__dropdown__item mpf-side-menu__dropdown__status">
                Não foi possível carregar.{' '}
                <button
                  type="button"
                  className="mpf-side-menu__dropdown__retry"
                  onClick={loadChildren}
                >
                  Tentar de novo
                </button>
              </li>
            )}
            {children?.map((subNode) => (
              <li
                key={subNode['@id']}
                className="mpf-side-menu__dropdown__item"
              >
                <ContextNavigationLink
                  props={subNode}
                  submenu
                  drill={hasChildren(subNode)}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </li>
  );
};

export const ContextNavigationComponent: React.FC<ContextNavigationProps> = ({
  navigation = {},
  showSearchInput = false,
}) => {
  const { items: rawItems = [], mode = 'accordion', filter } = navigation;
  const items = useMemo(() => filterOutMediaItems(rawItems), [rawItems]);
  const [openId, toggle] = useOpenItem(items);
  const [query, setQuery] = useState('');
  // O menu continua montado entre páginas, então o texto do filtro ficaria
  // lá ao voltar para a lista (ex.: "Voltar para Unidades"). Cada página nova
  // traz outro `items`, e o filtro recomeça vazio.
  useEffect(() => {
    setQuery('');
  }, [rawItems]);

  const navTitle =
    typeof navigation.title === 'string'
      ? { title: navigation.title }
      : navigation.title || { title: 'Navegação do contexto' };
  const parent = 'parent' in navTitle ? navTitle.parent : undefined;
  const rootUrl = 'relative_url' in navTitle ? navTitle.relative_url : '';
  const rootIsCurrent = 'is_current' in navTitle && !!navTitle.is_current;
  const rootTitle = (className: string) =>
    rootUrl ? (
      <MPFLink
        href={`/${rootUrl}`}
        className={`${className}-link${
          rootIsCurrent ? ` ${className}-link--active` : ''
        }`}
        aria-current={rootIsCurrent ? 'page' : undefined}
      >
        {navTitle.title}
      </MPFLink>
    ) : (
      navTitle.title
    );

  const filtering = !!filter && query.trim() !== '';
  const visibleItems = filtering
    ? items.filter((node) => matchesQuery(node.title, query))
    : items;

  return (
    <>
      {items.length ? (
        <aside className="mpf-side-menu">
          <div className="mpf-side-menu__content mpf-side-menu__content--navigation">
            {parent ? (
              <div className="mpf-side-menu__submenu-header">
                <MPFLink
                  href={`/${parent.relative_url}`}
                  className="mpf-side-menu__back"
                >
                  <ArrowLeftIcon aria-hidden="true" />
                  Voltar para {parent.title}
                </MPFLink>
                <span
                  className="mpf-side-menu__submenu-title"
                  role="heading"
                  aria-level={2}
                >
                  {rootTitle('mpf-side-menu__submenu-title')}
                </span>
              </div>
            ) : (
              <div className="mpf-side-menu__header">
                <span
                  className="mpf-side-menu__header__title"
                  role="heading"
                  aria-level={2}
                >
                  {rootTitle('mpf-side-menu__header__title')}
                </span>
              </div>
            )}
            {showSearchInput && (
              <form action="/search" className="mpf-side-menu__search">
                <input
                  type="text"
                  className="mpf-side-menu__header__search"
                  placeholder={`Buscar em ${navTitle.title}`}
                  aria-label={`Buscar em ${navTitle.title}`}
                  name="SearchableText"
                />
                {'relative_url' in navTitle && navTitle.relative_url && (
                  <input
                    type="hidden"
                    name="path.query"
                    value={`/${navTitle.relative_url}`}
                  />
                )}
              </form>
            )}
            {filter && (
              <MenuFilter
                label={filter.label}
                placeholder={filter.placeholder}
                value={query}
                onChange={setQuery}
                resultCount={filtering ? visibleItems.length : null}
              />
            )}
            <nav aria-label="Submenu" className="mpf-side-menu__nav">
              <ul className="mpf-side-menu__list">
                {mode === 'flat'
                  ? visibleItems.map((node) => (
                      <li
                        key={node['@id']}
                        className="mpf-side-menu__dropdown__item"
                      >
                        <ContextNavigationLink
                          props={node}
                          submenu
                          drill={hasChildren(node)}
                          label={
                            filtering
                              ? highlightQuery(node.title, query)
                              : undefined
                          }
                        />
                      </li>
                    ))
                  : visibleItems.map((node) =>
                      hasChildren(node) ? (
                        <DropdownItem
                          key={node['@id']}
                          node={node}
                          isOpen={openId === node['@id']}
                          onToggle={() => toggle(node['@id'] || '')}
                        />
                      ) : (
                        <li key={node['@id']} className="mpf-side-menu__item">
                          <ContextNavigationLink props={node} />
                        </li>
                      ),
                    )}
                {filtering && !visibleItems.length && (
                  <li className="mpf-side-menu__dropdown__status">
                    Nenhum resultado para “{query.trim()}”
                  </li>
                )}
              </ul>
            </nav>
          </div>
        </aside>
      ) : null}
    </>
  );
};

export default compose(
  withRouter,
  withContextNavigation,
)(ContextNavigationComponent);
